import type { XYPosition } from '@xyflow/react';
import type { IProjectAsset, IVideoClip } from '@packages/global';

import type {
  ClipCanvasFormType,
  ClipCanvasNode,
  ClipCanvasNodeActions,
  ClipCanvasResourceMediaType,
} from './types';
import { getAssetVoiceAudioUrl } from './referenceMedia';
import {
  buildSettingAssetNode,
  getDefaultSettingNodePosition,
  isFormGeneratorNode,
  isLegacyDefaultSettingNodePosition,
  isMediaResourceNode,
  isSettingAssetNode,
} from './clipCanvasNodeFactories';

const LAYOUT_STORAGE_VERSION = 1;
const INITIAL_PARAMS_STORAGE_MAX_STRING_LENGTH = 4096;
const INITIAL_PARAMS_STORAGE_MAX_ARRAY_LENGTH = 24;
const INITIAL_PARAMS_STORAGE_MAX_OBJECT_KEYS = 60;

export interface SavedClipCanvasLayout {
  version: number;
  positions: Record<string, XYPosition>;
  episodeCharactersAutoLoaded?: boolean;
  formNodes: Array<{
    id: string;
    type: ClipCanvasFormType;
    title: string;
    initialParams?: Record<string, unknown>;
    position: XYPosition;
  }>;
  settingNodes?: Array<{
    id: string;
    assetId?: string;
    /** legacy: 旧布局曾保存完整 asset 对象，读取时仅使用 id 迁移 */
    asset?: IProjectAsset;
    imageUrl?: string;
    audioUrl?: string;
    position: XYPosition;
  }>;
  mediaResourceNodes?: Array<{
    id: string;
    mediaType: ClipCanvasResourceMediaType;
    title: string;
    url: string;
    sourceLabel: string;
    prompt?: string;
    position: XYPosition;
  }>;
}

export function stableSerialize(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function getLayoutStorageKey(videoClip: Pick<IVideoClip, 'projectId' | 'episodeId' | 'id'>): string {
  return [
    'super-video-editor',
    'clip-canvas-layout',
    videoClip.projectId,
    videoClip.episodeId,
    videoClip.id,
  ].join(':');
}

export function readSavedLayout(storageKey: string): SavedClipCanvasLayout | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedClipCanvasLayout;
    return parsed.version === LAYOUT_STORAGE_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

function sanitizeValueForStorage(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return value.length > INITIAL_PARAMS_STORAGE_MAX_STRING_LENGTH
      ? value.slice(0, INITIAL_PARAMS_STORAGE_MAX_STRING_LENGTH)
      : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) {
    return value
      .slice(0, INITIAL_PARAMS_STORAGE_MAX_ARRAY_LENGTH)
      .map((item) => sanitizeValueForStorage(item, depth + 1));
  }
  if (typeof value === 'object') {
    if (depth >= 2) return undefined;
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .slice(0, INITIAL_PARAMS_STORAGE_MAX_OBJECT_KEYS)
        .map(([key, nestedValue]) => [key, sanitizeValueForStorage(nestedValue, depth + 1)])
        .filter(([, nestedValue]) => nestedValue !== undefined),
    );
  }
  return undefined;
}

function sanitizeInitialParamsForStorage(params?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const sanitized = sanitizeValueForStorage(params);
  return sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)
    ? sanitized as Record<string, unknown>
    : undefined;
}

function getSavedSettingAssetId(settingNode: NonNullable<SavedClipCanvasLayout['settingNodes']>[number]): string | undefined {
  return settingNode.assetId || settingNode.asset?.id;
}

export function buildRestoredSettingNodes(
  savedLayout: SavedClipCanvasLayout | null,
  episodeAssets: IProjectAsset[],
  videoClipId: string,
  actions: ClipCanvasNodeActions,
): ClipCanvasNode[] {
  if (!savedLayout?.settingNodes?.length) return [];
  const assetsById = new Map(episodeAssets.map((asset) => [asset.id, asset]));
  return savedLayout.settingNodes.flatMap((settingNode, index) => {
    const assetId = getSavedSettingAssetId(settingNode);
    const asset = assetId ? assetsById.get(assetId) : undefined;
    if (!asset) return [];
    return [buildSettingAssetNode(
      videoClipId,
      asset,
      isLegacyDefaultSettingNodePosition(settingNode.position)
        ? getDefaultSettingNodePosition(index)
        : settingNode.position,
      actions,
      settingNode.imageUrl || asset.conceptImageUrl,
      settingNode.audioUrl || getAssetVoiceAudioUrl(asset),
      settingNode.id.split(':').at(-1) || Date.now(),
    )];
  });
}

export function saveLayout(
  storageKey: string,
  nodes: ClipCanvasNode[],
  options?: { episodeCharactersAutoLoaded?: boolean },
) {
  if (typeof window === 'undefined') return;
  const previousLayout = readSavedLayout(storageKey);
  const positions = nodes.reduce<Record<string, XYPosition>>((acc, node) => {
    acc[node.id] = node.position;
    return acc;
  }, {});
  const formNodes = nodes
    .filter(isFormGeneratorNode)
    .map((node) => ({
      id: node.id,
      type: node.data.formType,
      title: node.data.title,
      initialParams: sanitizeInitialParamsForStorage(node.data.initialParams),
      position: node.position,
    }));
  const settingNodes = nodes
    .filter(isSettingAssetNode)
    .map((node) => ({
      id: node.id,
      assetId: node.data.asset.id,
      imageUrl: node.data.imageUrl,
      audioUrl: node.data.audioUrl,
      position: node.position,
    }));
  const mediaResourceNodes = nodes
    .filter(isMediaResourceNode)
    .map((node) => ({
      id: node.id,
      mediaType: node.data.mediaType,
      title: node.data.title,
      url: node.data.url,
      sourceLabel: node.data.sourceLabel,
      prompt: node.data.prompt,
      position: node.position,
    }));
  const payload: SavedClipCanvasLayout = {
    version: LAYOUT_STORAGE_VERSION,
    positions,
    episodeCharactersAutoLoaded: options?.episodeCharactersAutoLoaded
      ?? previousLayout?.episodeCharactersAutoLoaded,
    formNodes,
    settingNodes,
    mediaResourceNodes,
  };
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (err) {
    console.warn('保存无限画布布局失败，已跳过本次持久化:', err);
  }
}

export function getMaxFormNodeCounter(formNodes: SavedClipCanvasLayout['formNodes'] = []): number {
  return formNodes.reduce((max, node) => {
    const suffix = Number(node.id.split(':').at(-1));
    return Number.isFinite(suffix) ? Math.max(max, suffix) : max;
  }, 0);
}
